/**
 * Market Constants and Utilities
 * 
 * Central place for market definitions, country mappings, and related utilities
 */

// Market definitions
export const MARKETS = {
  ANZ: 'ANZ',
  USA: 'USA'
} as const

export type Market = typeof MARKETS[keyof typeof MARKETS]

// All available markets in order
export const ALL_MARKETS: Market[] = [MARKETS.ANZ, MARKETS.USA]

// Country to market mapping
export const COUNTRY_TO_MARKET_MAP: Record<string, Market> = {
  'AU': MARKETS.ANZ,
  'NZ': MARKETS.ANZ,
  'USA': MARKETS.USA,
  'US': MARKETS.USA  // Support both 'US' and 'USA' country codes
}

// Market display names
export const MARKET_DISPLAY_NAMES: Record<Market, string> = {
  [MARKETS.ANZ]: 'ANZ',
  [MARKETS.USA]: 'USA'
}

// Market descriptions
export const MARKET_DESCRIPTIONS: Record<Market, string> = {
  [MARKETS.ANZ]: 'Australia and New Zealand',
  [MARKETS.USA]: 'United States'
}

/**
 * Get market for a given country code
 */
export const getMarketForCountry = (country: string): Market | null => {
  return COUNTRY_TO_MARKET_MAP[country.toUpperCase()] || null
}

/**
 * Get all countries for a given market
 */
export const getCountriesForMarket = (market: Market): string[] => {
  return Object.entries(COUNTRY_TO_MARKET_MAP)
    .filter(([, marketValue]) => marketValue === market)
    .map(([country]) => country)
}

/**
 * Get market filter options for dropdowns
 */
export const getMarketFilterOptions = () => {
  return [
    { value: '', label: 'Global' },
    ...ALL_MARKETS.map(market => ({
      value: market,
      label: MARKET_DISPLAY_NAMES[market]
    }))
  ]
}

/**
 * Check if a country belongs to a specific market
 */
export const isCountryInMarket = (country: string, market: Market): boolean => {
  return getMarketForCountry(country) === market
}